import { createSlice } from "@reduxjs/toolkit";

import { InvitationsAPI } from "../../services/invitations/invitations.service";
import {
	IComment,
	IGallery,
	Invitations,
	InvitationLastPaymentTransaction,
	InvitationStats,
	IPayedInvitation,
	INVITATION_PRICING_PLANS_ENUM
} from "../../services/invitations/dto/invitations.dto";
import { RootState } from "../../index";
import { FilterOptionsResponse } from "../../services";
import { PLAN_NAMES } from "../../../constants";

interface invitationState {
	landingList: Invitations[];
	selectedInvitation: Invitations | undefined;
	selectedInvitationGallery: IGallery[] | undefined;
	showImageGallerySlides: boolean;
	initialSlideShowImage: number;
	managedInvitation: Invitations | undefined;
	errorManagedInvitation: unknown | undefined;
	invitationPayedData: IPayedInvitation;
	openPricingModal: boolean;
	openCheckoutDrawer: boolean;
	loadingManageInvitation: boolean;
	managedInvitationGallery: IGallery[] | undefined;
	loadingManagedInvitationGallery: boolean;
	selectedField: string | undefined;
	loadingInvitationComments: boolean;
	invitationComments: IComment[] | undefined;
	commentsPage: number | undefined;
	totalComments: number | undefined;
	myComment: IComment | undefined;
	stats: InvitationStats | undefined;
}

export const DefaultPricingPlan: IPayedInvitation = {
	isPayed: false,
	type: 0,
	name: "Draft",
	discount: 0,
	is_discount_percentage: false,
	amount: 0,
	currency: "EGP"
};
const initialState: invitationState = {
	landingList: [],
	selectedInvitation: undefined,
	selectedInvitationGallery: undefined,
	showImageGallerySlides: false,
	initialSlideShowImage: 0,
	managedInvitation: undefined,
	errorManagedInvitation: undefined,
	openPricingModal: false,
	openCheckoutDrawer: false,
	invitationPayedData: DefaultPricingPlan,
	loadingManageInvitation: false,
	managedInvitationGallery: undefined,
	selectedField: undefined,
	loadingManagedInvitationGallery: false,
	loadingInvitationComments: false,
	invitationComments: undefined,
	commentsPage: 1,
	totalComments: 10,
	myComment: undefined,
	stats: undefined
};

const InvitationsSlice = createSlice({
	name: "invitations",
	initialState,
	reducers: {
		setSelectedField: (state, action) => {
			state.selectedField = action.payload;
		},
		deSelectField: state => {
			state.selectedField = undefined;
		},
		resetInvitation: state => {
			state.selectedInvitation = undefined;
			state.managedInvitation = undefined;
			state.selectedInvitationGallery = undefined;
			state.managedInvitationGallery = undefined;
			state.invitationComments = undefined;
			state.stats = undefined;
		},
		resetMyComment: state => {
			state.myComment = undefined;
		},
		toggleImageGallerySlides: (state, action) => {
			state.initialSlideShowImage = action.payload ? action.payload : 0;
			state.showImageGallerySlides = !state.showImageGallerySlides;
		},
		setOpenPricingModal: (state, action) => {
			state.openPricingModal = action.payload;
		},
		setOpenCheckoutDrawer: (state, action) => {
			state.openCheckoutDrawer = action.payload;
		}
	},
	extraReducers: builder =>
		builder
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitations.matchFulfilled, (state, action) => {
				const { invitations } = action.payload as { invitations: Invitations[] };
				state.landingList = invitations;
			})
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitation.matchFulfilled, (state, action) => {
				const { invitation } = action.payload as { invitation: Invitations };
				state.selectedInvitation = invitation;
			})
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitationGallery.matchFulfilled, (state, action) => {
				const { gallery } = action.payload as { gallery: IGallery[] };
				state.selectedInvitationGallery = gallery;
			})
			.addMatcher(InvitationsAPI.endpoints.getManageWeddingInvitation.matchPending, state => {
				state.loadingManageInvitation = true;
			})
			.addMatcher(InvitationsAPI.endpoints.getManageWeddingInvitation.matchFulfilled, (state, action) => {
				const { invitation } = action.payload as { invitation: Invitations };
				state.managedInvitation = invitation;
				state.loadingManageInvitation = false;
			})
			.addMatcher(InvitationsAPI.endpoints.getInvitationLastPayment.matchFulfilled, (state, action) => {
				const { planId, oldPayedAmount } = action.payload as {
					planId: INVITATION_PRICING_PLANS_ENUM;
					oldPayedAmount: number;
				};
				state.invitationPayedData =
					planId > 0
						? ({
								amount: oldPayedAmount,
								type: planId,
								name: PLAN_NAMES[planId],
								isPayed: true
							} as IPayedInvitation)
						: (DefaultPricingPlan as IPayedInvitation);
			})
			.addMatcher(InvitationsAPI.endpoints.getManageWeddingInvitation.matchRejected, (state, action) => {
				state.errorManagedInvitation = action.payload;
				state.loadingManageInvitation = false;
			})
			.addMatcher(InvitationsAPI.endpoints.getManageWeddingInvitationGallery.matchPending, state => {
				state.loadingManagedInvitationGallery = true;
			})
			.addMatcher(InvitationsAPI.endpoints.getManageWeddingInvitationGallery.matchFulfilled, (state, action) => {
				const { gallery } = action.payload as { gallery: IGallery[] };
				state.managedInvitationGallery = gallery;
				state.loadingManagedInvitationGallery = false;
			})
			.addMatcher(InvitationsAPI.endpoints.getManageWeddingInvitationGallery.matchRejected, state => {
				state.loadingManagedInvitationGallery = false;
			})
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitationComments.matchPending, state => {
				state.loadingInvitationComments = true;
			})
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitationComments.matchFulfilled, (state, action) => {
				const { items, totalItems, page } = action.payload as FilterOptionsResponse<IComment[]>;
				state.invitationComments = items;
				state.totalComments = totalItems;
				state.commentsPage = page;
				state.loadingInvitationComments = false;
			})
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitationComment.matchFulfilled, (state, action) => {
				const { comment } = action.payload as { comment: IComment };
				state.myComment = comment;
			})
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitationComments.matchRejected, state => {
				state.loadingInvitationComments = false;
			})
			.addMatcher(InvitationsAPI.endpoints.getWeddingInvitationStats.matchFulfilled, (state, action) => {
				const { total_views, total_responses, accepted_responses, rejected_responses, total_memories } =
					action.payload as InvitationStats;
				state.stats = { total_views, total_responses, accepted_responses, rejected_responses, total_memories };
			})
});

export const getFullInvitationsState = (state: RootState): invitationState => state.invitations;

export const {
	setSelectedField,
	deSelectField,
	resetInvitation,
	resetMyComment,
	toggleImageGallerySlides,
	setOpenPricingModal,
	setOpenCheckoutDrawer
} = InvitationsSlice.actions;

export default InvitationsSlice.reducer;
